import type { WidenMediaElement } from '@libs/widen-utils';
import type { PriceFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { Measurement } from '@models/graphql';
import type { SharedProductPagePropsPrices } from '@product/ProductPage/ProductPage';

export interface CfCaseProductBoxProps {
  sku: string;
  pricePerUnit?: number;
  currencyCode: string;
  title: string;
  subTitle?: string;
  image?: WidenMediaElement;
  sorting: number;
  caseCode: string;
  design?: string;
}

export const enhanceCfCaseProductBoxPropsWithPrices = (
  cfCaseProductProps: CfCaseProductBoxProps,
  priceOverwriteProps: SharedProductPagePropsPrices | undefined
): CfCaseProductBoxProps => {
  // overwrite prices e.g. for dynamic price regarding users priceCountry
  const priceOverwrite = priceOverwriteProps?.associatedProductPrices.find(p => p.id === cfCaseProductProps.sku);
  if (priceOverwrite && priceOverwrite.baseUnitCentPrice != null) {
    const pricePerUnit = priceOverwrite.baseUnitCentPrice;
    const currencyCode = priceOverwrite.quantityDiscounts[0].currency_code ?? null;
    return { ...cfCaseProductProps, pricePerUnit, currencyCode };
  }

  return cfCaseProductProps;
};

export const getCFCasePrice = (price: PriceFragmentEnhanced) => ({
  pricePerUnit: price?.price ? price.price * 100 : undefined, // pricePerUnit is expected in cents
  currencyCode: price?.currency_code ?? undefined,
});

export const findMatchingCFCaseProducts = (
  currentProduct: ProductFragmentEnhanced,
  cfCasesOfCurrentProduct: CfCaseProductBoxProps[],
  cfCaseLengthOfCurrentProduct: Record<string, Measurement<number>> | null,
  length: number
): CfCaseProductBoxProps[] | undefined => {
  if (!cfCasesOfCurrentProduct || cfCasesOfCurrentProduct.length < 1) {
    return undefined;
  }

  if (!cfCaseLengthOfCurrentProduct) {
    return undefined;
  }

  return cfCasesOfCurrentProduct.filter(cfCase => {
    const minLengthPropName = `minCase${cfCase.caseCode}`;
    const maxLengthPropName = `maxCase${cfCase.caseCode}`;
    const minLength = cfCaseLengthOfCurrentProduct[minLengthPropName];
    const maxLength = cfCaseLengthOfCurrentProduct[maxLengthPropName];

    const hasCaseMetrics =
      minLength?.value !== null &&
      minLength?.value !== undefined &&
      maxLength?.value !== null &&
      maxLength?.value !== undefined;
    if (!hasCaseMetrics) {
      return false;
    }

    return length >= minLength?.value && length <= maxLength?.value;
  });
};
